import { useCallback, useEffect, useState } from 'react'; 
import { Outlet } from 'react-router-dom'; 
import { useKeycloak } from '@react-keycloak/web'; 
import { setKeycloakInstance } from '../service/api';

const ProtectedKeycloakRoutes = () => { 
  const { keycloak, initialized } = useKeycloak(); 
  const [userInfo, setUserInfo] = useState({}); 
  const [isLoading, setIsLoading] = useState(true);
 
  // Set keycloak instance in API module when available
  useEffect(() => {
    if (initialized && keycloak) {
      setKeycloakInstance(keycloak);
      setIsLoading(false);
    }
  }, [initialized, keycloak]);

  // Fetch user info when initialized & authenticated 
  const fetchUserInfo = useCallback(async () => { 
    if (initialized && keycloak.authenticated) { 
      try {
        const { name, iconpath, email } = await keycloak.loadUserInfo(); 
        setUserInfo({ name, iconpath, email }); 
      } catch (error) {
        console.error('Failed to load user info:', error);
      }
    } 
  }, [initialized, keycloak]); 
 
  useEffect(() => { 
    fetchUserInfo(); 
  }, [fetchUserInfo]); 
 
  // Save user info to localStorage when available 
  useEffect(() => { 
    if (keycloak.authenticated && userInfo.email) { 
      const userStr = JSON.stringify({ 
        useremail: userInfo.email, 
        iconpath: userInfo.iconpath, 
      }); 
      localStorage.setItem('User', userStr); 
    } 
  }, [keycloak.authenticated, userInfo]); 

  // Handle token updates - save to localStorage for other parts of app that might need it
  useEffect(() => {
    if (keycloak?.token) {
      localStorage.setItem('token', `Bearer ${keycloak.token}`);
    }
  }, [keycloak?.token]);

  // Set up token refresh monitoring (optional - the API interceptor handles this now)
  useEffect(() => {
    if (!keycloak?.tokenParsed || !keycloak.authenticated) return;

    const monitorToken = () => {
      const { exp } = keycloak.tokenParsed;
      if (exp) {
        const expiresIn = exp * 1000 - Date.now();
        // Log token status for debugging
        if (expiresIn < 300000) { // Less than 5 minutes
          console.log(`Token expires in ${Math.round(expiresIn / 1000 / 60)} minutes`);
        }
      }
    };

    const interval = setInterval(monitorToken, 60000); // Check every minute
    monitorToken(); // Run once immediately

    return () => clearInterval(interval);
  }, [keycloak]);

  // Show loading while initializing
  if (!initialized || isLoading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="text-lg">Loading...</div>
      </div>
    );
  }
 
  // Show content only if authenticated 
  if (keycloak.authenticated) { 
    return <Outlet />; 
  } 
 
  // Not authenticated - keycloak will handle redirect to login
  return (
    <div className="flex items-center justify-center min-h-screen">
      <div className="text-lg">Redirecting to login...</div>
    </div>
  );
}; 
 
export default ProtectedKeycloakRoutes;